import { contactBlock } from "./legal";

export const shippingPolicy = {
  title: "Shipping & Delivery Policy",
  updated: "14 September 2026",
  heroCopy:
    "Learn how Bytevora delivers software, digital services, project files, credentials, cloud deployments, and other technology deliverables.",
  toc: [
    { label: "Digital Delivery", id: "digital-delivery" },
    { label: "Delivery Timelines", id: "delivery-timelines" },
    { label: "Client Responsibilities", id: "client-responsibilities-affecting-delivery" },
    { label: "Software & Source Code", id: "delivery-of-software-and-source-code" },
    { label: "Web & Mobile Delivery", id: "website-and-web-application-delivery" },
    { label: "Cloud & Infrastructure", id: "cloud-and-infrastructure-delivery" },
    { label: "Milestones", id: "milestone-based-delivery" },
    { label: "Acceptance", id: "review-and-acceptance" },
    { label: "Third-Party Delays", id: "delays-caused-by-third-parties" },
    { label: "Access & Handover", id: "credentials-and-access" },
    { label: "Contact", id: "contact-us" },
  ],
  intro: [
    "This Shipping & Delivery Policy explains how Bytevora IT Solutions Private Limited (“Bytevora”, “we”, “us”, or “our”) delivers digital services, software, project files, access credentials, cloud deployments, technical documents, and other agreed deliverables.",
    "Bytevora primarily provides digital and technology services. Unless expressly stated otherwise, we do not ship physical products.",
    "By purchasing services from Bytevora or entering into a project agreement with us, you acknowledge and agree to this Policy together with our applicable Terms & Conditions, quotation, proposal, invoice, or service agreement.",
  ],
  sections: [
    {
      id: "scope-of-this-policy",
      title: "Scope of This Policy",
      blocks: [
        { type: "p", text: "This Policy applies to digital delivery associated with services such as:" },
        {
          type: "list",
          items: ["Software development", "Web development", "Mobile application development", "SaaS and product development", "AI and automation solutions", "Cloud and infrastructure services", "Digital transformation", "Technology consulting", "Technical support", "Maintenance", "Design files", "Source code", "Documentation", "Reports", "Credentials", "Hosted systems", "Other agreed digital deliverables"],
        },
        { type: "p", text: "Where a separate written agreement contains specific delivery terms, those project-specific terms will generally take precedence." },
      ],
    },
    {
      id: "digital-delivery",
      title: "Digital Delivery",
      blocks: [
        { type: "p", text: "Most Bytevora services are delivered electronically." },
        { type: "p", text: "Delivery methods may include email, secure download links, cloud storage, Git repositories, client portals, project management platforms, hosting environments, production deployments, staging environments, source-code repositories, shared documents, secure credential-sharing tools, or other mutually agreed digital channels." },
        { type: "p", text: "The delivery method may vary depending on the nature and security requirements of the project." },
      ],
    },
    {
      id: "no-physical-shipping-by-default",
      title: "No Physical Shipping by Default",
      blocks: [
        { type: "p", text: "Bytevora does not generally sell or ship physical goods." },
        { type: "p", text: "Accordingly, traditional shipping concepts such as courier delivery, postal shipping, tracking numbers, physical shipping charges, international freight, and parcel delivery normally do not apply to our services." },
        { type: "p", text: "If Bytevora ever agrees to provide a physical item, equipment, document, or other tangible product, the applicable shipping terms will be communicated separately before purchase or dispatch." },
      ],
    },
    {
      id: "delivery-timelines",
      title: "Delivery Timelines",
      blocks: [
        { type: "p", text: "Delivery timelines are determined by the specific project scope." },
        { type: "p", text: "Estimated delivery dates may be stated in quotations, proposals, statements of work, project schedules, order confirmations, contracts, or written communications." },
        { type: "p", text: "Because many Bytevora services are custom-developed, there is no single standard delivery time applicable to all projects." },
        { type: "p", text: "Delivery time may depend on project scope, complexity, features, integrations, client approvals, content availability, technical requirements, third-party dependencies, infrastructure provisioning, testing requirements, change requests, and project stage." },
      ],
    },
    {
      id: "estimated-timelines",
      title: "Estimated Timelines",
      blocks: [
        { type: "p", text: "Any delivery timeline provided before or during a project is generally an estimate unless expressly stated as a guaranteed contractual deadline." },
        { type: "p", text: "Estimated timelines may change if requirements change, additional features are requested, client approvals are delayed, required content is not provided, credentials or technical access are delayed, third-party APIs are unavailable, infrastructure issues occur, technical complexity is discovered, external providers experience outages, or other events outside Bytevora’s reasonable control occur." },
        { type: "p", text: "Bytevora will make reasonable efforts to communicate material delivery delays." },
      ],
    },
    {
      id: "client-responsibilities-affecting-delivery",
      title: "Client Responsibilities Affecting Delivery",
      blocks: [
        { type: "p", text: "Timely delivery often depends on client cooperation." },
        { type: "p", text: "Clients may be required to provide project requirements, content, branding assets, images, product information, technical documentation, server credentials, domain access, API credentials, third-party account access, feedback, approvals, testing responses, and legal or compliance information." },
        { type: "p", text: "If required information is delayed, the project timeline may also be delayed." },
      ],
    },
    {
      id: "delivery-of-software-and-source-code",
      title: "Delivery of Software and Source Code",
      blocks: [
        { type: "p", text: "Where source code is included in the agreed scope, delivery may occur through GitHub, GitLab, Bitbucket, private Git repositories, secure archive files, client-owned repositories, or other approved source-control platforms." },
        { type: "p", text: "Source code may be transferred upon completion of the applicable milestone, final approval, receipt of required payment, or satisfaction of project-specific contractual conditions." },
        { type: "p", text: "Ownership and licence rights are governed by the applicable agreement." },
      ],
    },
    {
      id: "website-and-web-application-delivery",
      title: "Website and Web Application Delivery",
      blocks: [
        { type: "p", text: "Website and web application projects may be delivered through deployment to the client’s hosting environment, deployment to a Bytevora-managed environment, migration to production, source-code handover, administrative credentials, project documentation, or repository access." },
        { type: "p", text: "Delivery may be considered complete when the agreed website or application is deployed, transferred, or otherwise made available according to the project scope." },
      ],
    },
    {
      id: "mobile-application-delivery",
      title: "Mobile Application Delivery",
      blocks: [
        { type: "p", text: "Mobile application projects may be delivered through source-code repository access, APK or other build files, test builds, App Store submissions, Google Play submissions, TestFlight or equivalent testing systems, or deployment documentation." },
        { type: "p", text: "App-store publishing may also depend on approvals by third-party platforms such as Apple or Google." },
        { type: "p", text: "Bytevora cannot guarantee approval timelines controlled by third-party app stores." },
      ],
    },
    {
      id: "cloud-and-infrastructure-delivery",
      title: "Cloud and Infrastructure Delivery",
      blocks: [
        { type: "p", text: "Cloud, server, hosting, and infrastructure services may be delivered through provisioned cloud resources, server configuration, deployment pipelines, infrastructure configuration, hosting setup, access credentials, architecture documentation, monitoring configuration, or other technical setup defined in the project scope." },
        { type: "p", text: "Delivery may depend on third-party providers and infrastructure availability." },
      ],
    },
    {
      id: "saas-and-hosted-services",
      title: "SaaS and Hosted Services",
      blocks: [
        { type: "p", text: "For SaaS or hosted services, delivery may occur through provision of account access, login credentials, workspace access, tenant activation, subscription activation, API credentials, or hosted application access." },
        { type: "p", text: "The service may be considered delivered when access has been successfully provisioned and communicated to the client." },
      ],
    },
    {
      id: "design-and-digital-files",
      title: "Design and Digital Files",
      blocks: [
        { type: "p", text: "Design or creative deliverables may be supplied in agreed formats such as PNG, JPG, PDF, SVG, Figma, source design files, or other agreed file formats." },
        { type: "p", text: "The specific file formats included will depend on the project agreement." },
      ],
    },
    {
      id: "documentation-and-reports",
      title: "Documentation and Reports",
      blocks: [
        { type: "p", text: "Consulting, audits, technical reviews, and planning engagements may be delivered through PDF documents, online documents, presentations, technical reports, architecture documents, recommendations, written summaries, meeting notes, or recorded sessions where agreed." },
        { type: "p", text: "Delivery is complete when the agreed material has been made available to the client." },
      ],
    },
    {
      id: "credentials-and-access",
      title: "Credentials and Access",
      blocks: [
        { type: "p", text: "Where delivery involves credentials, Bytevora may use secure methods to share login details, API keys, server credentials, administrative accounts, repository access, or cloud access." },
        { type: "p", text: "Clients are responsible for securing credentials after handover." },
        { type: "p", text: "Bytevora may recommend that passwords or credentials be changed after delivery." },
      ],
    },
    {
      id: "milestone-based-delivery",
      title: "Milestone-Based Delivery",
      blocks: [
        { type: "p", text: "Projects may be delivered in stages, such as discovery, design, prototype, development, testing, deployment, and final handover." },
        { type: "p", text: "Each milestone may have its own deliverables, review process, approval requirements, payment requirements, and timeline." },
        { type: "p", text: "Completion of one milestone does not necessarily mean completion of the entire project." },
      ],
    },
    {
      id: "review-and-acceptance",
      title: "Review and Acceptance",
      blocks: [
        { type: "p", text: "Bytevora may provide deliverables for client review before final acceptance." },
        { type: "p", text: "Clients should review deliverables within the timeframe specified in the applicable agreement or within a reasonable period." },
        { type: "p", text: "If issues are identified, the client should provide clear feedback so Bytevora can review them." },
        { type: "p", text: "Delays in client review or approval may affect the final delivery schedule." },
      ],
    },
    {
      id: "revisions",
      title: "Revisions",
      blocks: [
        { type: "p", text: "Where revisions are included in the project scope, Bytevora will make revisions in accordance with the applicable agreement." },
        { type: "p", text: "Requests beyond the agreed revision scope may be treated as additional work and may require additional fees, extended timelines, change requests, or revised scope." },
      ],
    },
    {
      id: "delivery-after-final-payment",
      title: "Delivery After Final Payment",
      blocks: [
        { type: "p", text: "For certain projects, final handover may be conditional upon full payment." },
        { type: "p", text: "Where permitted by the applicable agreement, Bytevora may withhold source-code transfer, final production credentials, editable design files, final documentation, administrative access, or ownership transfer until outstanding invoices are paid." },
      ],
    },
    {
      id: "delays-caused-by-third-parties",
      title: "Delays Caused by Third Parties",
      blocks: [
        { type: "p", text: "Some projects depend on third-party services, including cloud providers, hosting providers, payment gateways, APIs, app stores, domain registrars, SaaS platforms, email providers, or communication providers." },
        { type: "p", text: "Bytevora is not responsible for delays caused solely by independent third-party providers." },
        { type: "p", text: "Where possible, we will assist with mitigation or alternative solutions." },
      ],
    },
    {
      id: "app-store-and-platform-approvals",
      title: "App Store and Platform Approvals",
      blocks: [
        { type: "p", text: "Submission to a third-party platform does not guarantee approval. Examples include the Apple App Store, Google Play, cloud marketplaces, payment gateway approval, or third-party SaaS marketplaces." },
        { type: "p", text: "Approval decisions and timelines are controlled by the relevant provider." },
        { type: "p", text: "Bytevora may assist with submission, but cannot guarantee approval." },
      ],
    },
    {
      id: "delayed-delivery-due-to-client-inactivity",
      title: "Delayed Delivery Due to Client Inactivity",
      blocks: [
        { type: "p", text: "If a client becomes unresponsive or fails to provide required information, approvals, or access, Bytevora may pause the project." },
        { type: "p", text: "Where a project remains inactive for an extended period, the delivery schedule may need to be revised, previously reserved resources may no longer be available, reactivation may require a new timeline, and additional costs may apply where appropriate." },
        { type: "p", text: "Specific inactivity terms may be stated in the project agreement." },
      ],
    },
    {
      id: "delivery-confirmation",
      title: "Delivery Confirmation",
      blocks: [
        { type: "p", text: "Delivery may be confirmed through email, project management update, repository access, deployment confirmation, shared file notification, client portal notification, or written acknowledgement." },
        { type: "p", text: "Clients should retain relevant delivery communications for their records." },
      ],
    },
    {
      id: "access-problems",
      title: "Access Problems",
      blocks: [
        { type: "p", text: "If you are unable to access a delivered file, repository, account, deployment, or digital resource, contact Bytevora promptly." },
        { type: "p", text: "We may verify your identity, project details, email address, account ownership, or access permissions before reissuing or restoring access." },
      ],
    },
    {
      id: "lost-digital-files",
      title: "Lost Digital Files",
      blocks: [
        { type: "p", text: "After successful delivery, clients are responsible for maintaining appropriate copies or backups of delivered materials." },
        { type: "p", text: "Unless otherwise agreed, Bytevora does not guarantee indefinite retention of source files, design files, documentation, builds, archives, credentials, or project backups." },
        { type: "p", text: "Clients should securely store delivered assets after handover." },
      ],
    },
    {
      id: "shipping-charges",
      title: "Shipping Charges",
      blocks: [
        { type: "p", text: "Because Bytevora primarily provides digital services, standard shipping charges normally do not apply." },
        { type: "p", text: "However, charges may apply for third-party infrastructure, cloud services, domain registration, hosting, software licences, API usage, paid integrations, app-store fees, or physical delivery if specifically requested." },
        { type: "p", text: "Such charges will generally be communicated in the relevant quotation or agreement." },
      ],
    },
    {
      id: "international-delivery",
      title: "International Delivery",
      blocks: [
        { type: "p", text: "Digital services may be delivered to clients outside India through electronic means." },
        { type: "p", text: "No physical customs or international shipping charges generally apply to digital delivery." },
        { type: "p", text: "However, clients may be responsible for applicable taxes, foreign exchange fees, international payment charges, or local regulatory requirements depending on the transaction and jurisdiction." },
      ],
    },
    {
      id: "no-delivery-to-incorrect-information",
      title: "No Delivery to Incorrect Information",
      blocks: [
        { type: "p", text: "Clients are responsible for providing accurate email addresses, contact details, repository usernames, account information, domain information, server details, and delivery instructions." },
        { type: "p", text: "Bytevora is not responsible for delays caused by incorrect or incomplete information provided by the client." },
      ],
    },
    {
      id: "delivery-and-refunds",
      title: "Delivery and Refunds",
      blocks: [
        {
          type: "p",
          segments: [
            { t: "Delivery-related refund matters are governed by our " },
            { link: "Refund & Cancellation Policy", to: "/refund-cancellation-policy" },
            { t: "." },
          ],
        },
        { type: "p", text: "A delay does not automatically create a refund entitlement, particularly where the delay results from client actions, scope changes, third-party providers, technical dependencies, or force majeure events." },
        { type: "p", text: "Refund eligibility will depend on the applicable policy and project agreement." },
      ],
    },
    {
      id: "force-majeure",
      title: "Force Majeure",
      blocks: [
        { type: "p", text: "Bytevora will not be responsible for delivery delays caused by events beyond its reasonable control, including natural disasters, government action, war, civil unrest, cyber incidents, major internet failures, power outages, cloud-provider outages, telecommunications failures, labour disruptions, or other comparable events, where permitted by law." },
      ],
    },
    {
      id: "relationship-with-other-agreements",
      title: "Relationship With Other Agreements",
      blocks: [
        { type: "p", text: "This Policy should be read together with the Terms & Conditions, Refund & Cancellation Policy, Privacy Policy, Cookie Policy, Disclaimer, and any applicable quotation, proposal, statement of work, service agreement, or contract." },
        { type: "p", text: "If a specific signed agreement contains delivery terms that differ from this general website Policy, the specific agreement will generally govern the relevant project." },
      ],
    },
    {
      id: "changes-to-this-policy",
      title: "Changes to This Policy",
      blocks: [
        { type: "p", text: "Bytevora may update this Shipping & Delivery Policy from time to time to reflect changes in services, delivery methods, platforms, business practices, payment models, or legal requirements." },
        { type: "p", text: "The latest version will be published on this page with an updated revision date." },
      ],
    },
    {
      id: "contact-us",
      title: "Contact Us",
      blocks: [
        { type: "p", text: "For questions about delivery, project handover, digital access, or this Policy, please contact:" },
        contactBlock,
      ],
    },
  ],
};
